import { useContext, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Spinner,
  Modal,
  Button,
} from "react-bootstrap";
import axios from "axios";
import { API } from "../../global/global";
import { AuthContext } from "../../context/AuthContext";

interface SelfieItem {
  servicio_id: number;
  actividad_id: number | null;
  usuario_id: number | null;
  usuario_nombre: string;
  latitud: string | null;
  longitud: string | null;
  archivo: string;
  url: string;
  fecha: string;
}

const SelfiesGaleria = () => {
  const authContext = useContext(AuthContext);
  const [selfies, setSelfies] = useState<SelfieItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtroServicio, setFiltroServicio] = useState<string>("");
  const [filtroFecha, setFiltroFecha] = useState<string>("");
  const [modalImg, setModalImg] = useState<string | null>(null);

  // Cargar selfies cuando cambian los filtros
  useEffect(() => {
    if (!filtroServicio && !filtroFecha) {
      setSelfies([]);
      return;
    }
    const fetchSelfies = async () => {
      setLoading(true);
      try {
        let url = `${API}/selfies?`;
        if (filtroServicio) url += `servicio_id=${filtroServicio}&`;
        if (filtroFecha) url += `fecha=${filtroFecha}&`;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${authContext.token}`,
          },
        });
        setSelfies(response.data);
      } catch (error) {
        console.error("Error al cargar selfies:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSelfies();
  }, [filtroServicio, filtroFecha, authContext.token]);

  return (
    <Container style={{ marginTop: "80px" }}>
      <h3 style={{ marginBottom: "20px" }}>Selfies de asistencia</h3>

      {/* Filtros */}
      <Row className="mb-4">
        <Col md={4}>
          <Form.Group>
            <Form.Label>ID Servicio</Form.Label>
            <Form.Control
              type="number"
              placeholder="Buscar por ID de servicio"
              value={filtroServicio}
              onChange={(e) => setFiltroServicio(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Fecha</Form.Label>
            <Form.Control
              type="date"
              value={filtroFecha}
              onChange={(e) => setFiltroFecha(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <Button
            variant="outline-secondary"
            onClick={() => {
              setFiltroServicio("");
              setFiltroFecha("");
            }}
          >
            Limpiar filtros
          </Button>
        </Col>
      </Row>

      {/* Galería */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : selfies.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">No se encontraron selfies con los filtros seleccionados</p>
        </div>
      ) : (
        <Row>
          {selfies.map((selfie, index) => (
            <Col key={index} xs={6} md={4} lg={3} className="mb-3">
              <Card
                style={{ cursor: "pointer" }}
                onClick={() => setModalImg(selfie.url)}
              >
                <Card.Img
                  variant="top"
                  src={selfie.url}
                  style={{ height: "200px", objectFit: "cover" }}
                />
                <Card.Body style={{ padding: "8px" }}>
                  <Card.Text style={{ fontSize: "12px", marginBottom: "2px" }}>
                    <strong>{selfie.usuario_nombre}</strong>
                  </Card.Text>
                  <Card.Text style={{ fontSize: "11px", color: "#666", marginBottom: "2px" }}>
                    Servicio: {selfie.servicio_id} | Actividad: {selfie.actividad_id ?? "N/A"}
                  </Card.Text>
                  {selfie.latitud && selfie.longitud && (
                    <Card.Text style={{ fontSize: "11px", marginBottom: "2px" }}>
                      <a
                        href={`https://www.google.com/maps?q=${selfie.latitud},${selfie.longitud}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        📍 {selfie.latitud}, {selfie.longitud}
                      </a>
                    </Card.Text>
                  )}
                  <Card.Text style={{ fontSize: "11px", color: "#666" }}>
                    {selfie.fecha}
                  </Card.Text>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mt-1 w-100"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const mapsUrl = `https://www.google.com/maps?q=${selfie.latitud},${selfie.longitud}`;
                        const html = `<div><a href="${selfie.url}"><img src="${selfie.url}" style="max-width:400px;" /></a><br/><a href="${mapsUrl}">📍 ${selfie.latitud}, ${selfie.longitud}</a></div>`;
                        const htmlBlob = new Blob([html], { type: "text/html" });
                        const textBlob = new Blob([`${selfie.url}\n📍 ${selfie.latitud}, ${selfie.longitud}\n${mapsUrl}`], { type: "text/plain" });
                        await navigator.clipboard.write([
                          new ClipboardItem({
                            "text/html": htmlBlob,
                            "text/plain": textBlob,
                          }),
                        ]);
                        alert("Copiado al portapapeles");
                      } catch (error) {
                        console.error("Error al copiar:", error);
                        navigator.clipboard.writeText(
                          `${selfie.url}\n📍 ${selfie.latitud ?? "N/A"}, ${selfie.longitud ?? "N/A"}\nhttps://www.google.com/maps?q=${selfie.latitud},${selfie.longitud}`
                        );
                        alert("Copiado como texto");
                      }
                    }}
                  >
                    📋 Copiar foto y ubicación
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Modal para ver imagen completa */}
      <Modal
        show={modalImg !== null}
        onHide={() => setModalImg(null)}
        centered
        size="lg"
      >
        <Modal.Body className="p-0">
          {modalImg && (
            <img
              src={modalImg}
              alt="Selfie"
              style={{ width: "100%", height: "auto" }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModalImg(null)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SelfiesGaleria;
